import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as NavLink, useNavigate } from "react-router";

import * as Api from "@/api";
import InputText from "@/design/forms/InputText";
import { showSnackbar } from "@/store/reducers/app";
import { setAuth, setJwtToken } from "@/store/reducers/auth";
import Valkyrie, { viArrowRight, viArrowRightToBracket, viMin, viSpinnerThird } from "@sippy-platform/valkyrie";

import { OTPField } from "@base-ui/react/otp-field";
import { Alert, Box, Button, Link, Stack, Typography } from "@mui/material";

import AuthUI from "./AuthUI";
import TotpTimer from "./TotpTimer";

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pbx = useSelector((state) => state.domain.pbx);
  const auth = useSelector((state) => state.auth);

  const [loginView, setLoginView] = useState("login");

  const [errorEmail, setErrorEmail] = useState("");
  const [errorPassword, setErrorPassword] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [totpChallenge, setTotpChallenge] = useState({});
  const [verificationCode, setVerificationCode] = useState("");
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (auth.id) {
      navigate("/backstage/home");
    }
  }, [navigate, auth.id, pbx]);

  // Handle the login
  const handleLogin = async (event) => {
    event.preventDefault();

    setErrorEmail(email.length < 1 ? "Required field" : "");
    setErrorPassword(password.length < 1 ? "Required field" : "");

    if (email.length > 1 && password.length > 1) {
      setLoading(true);

      await Promise.resolve(Api.postLogin(email, password))
        .then((response) => {
          if (response.data?.twoStepAuthChallenge) {
            setTotpChallenge(response.data.twoStepAuthChallenge);
            setLoginView("totp");
          } else {
            dispatch(setJwtToken(response.data.token));
            dispatch(setAuth(response.data.account));
          }
        })
        .catch(() => {
          dispatch(showSnackbar("Please verify your credentials."));
          setPassword("");
        })
        .finally(() => setLoading(false));
    }
  };

  const handleTotp = async (event) => {
    event.preventDefault();

    setLoading(true);

    await Promise.resolve(
      Api.postLoginTotp({
        code: verificationCode,
        token: totpChallenge.token,
      }),
    )
      .then((response) => {
        dispatch(setJwtToken(response.data.token));
        dispatch(setAuth(response.data.account));
      })
      .catch(() => {
        dispatch(showSnackbar("Please verify your credentials."));
        setPassword("");
      })
      .finally(() => setLoading(false));
  };

  function cancelAuthentication() {
    setLoginView("login");

    setErrorEmail("");
    setErrorPassword("");
    setEmail("");
    setPassword("");
    setLoading(false);
    setRecoveryMode(false);

    setTotpChallenge({});
    setVerificationCode("");
  }

  // 2 Factor Authentication
  const expiresMs = new Date(totpChallenge.expires).getTime();
  const totpExpired = !isNaN(expiresMs) && expiresMs <= now;

  useEffect(() => {
    if (isNaN(expiresMs)) {
      return undefined;
    }

    const remainingMs = expiresMs - Date.now();

    if (remainingMs <= 0) {
      return undefined;
    }

    const timeout = setTimeout(
      () => {
        setNow(Date.now());
      },
      Math.max(remainingMs, 0),
    );

    return () => clearTimeout(timeout);
  }, [expiresMs]);

  return (
    <>
      <title>{`Login \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      {loginView === "login" ? (
        <AuthUI onSubmit={handleLogin} key="login">
          <Typography variant="h4" sx={{ m: 0, mb: 2 }}>
            Welcome
          </Typography>
          <InputText
            required
            placeholder="Email"
            id="email"
            type="email"
            label="Email"
            value={email}
            onChange={(_, value) => setEmail(value)}
            helperText={errorEmail}
          />
          <InputText
            type="password"
            required
            placeholder="Password"
            id="password"
            label="Password"
            value={password}
            onChange={(_, value) => setPassword(value)}
            helperText={errorPassword}
          />
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
            }}
          >
            <Link component={NavLink} to="/password-forgotten" color="inherit" underline="hover" variant="body2">
              Forgot password?
            </Link>
          </Stack>
          <Button
            key="loginSubmit"
            variant="contained"
            size="large"
            id="submit"
            type="submit"
            endIcon={<Valkyrie icon={loading ? viSpinnerThird : viArrowRightToBracket} spin={loading} />}
            disabled={loading}
          >
            Login
          </Button>
        </AuthUI>
      ) : (
        <AuthUI onSubmit={handleTotp} key="totp">
          <Box>
            <Typography variant="h4" sx={{ m: 0 }}>
              Verification code
            </Typography>
            <Typography sx={{ m: 0, mb: 2 }}>{email}</Typography>
          </Box>
          {totpExpired && <Alert severity="error">The session has expired. Go back to login to try again.</Alert>}
          {!recoveryMode ? (
            <div className="flex w-full max-w-80 min-w-0 flex-col items-start gap-1">
              <label htmlFor="otp" className="text-xs font-normal text-zinc-500 dark:text-zinc-400">
                Verification code *
              </label>
              <OTPField.Root
                id="otp"
                length={6}
                className="flex w-full min-w-0 items-center gap-3"
                value={verificationCode}
                onValueChange={setVerificationCode}
                disabled={totpExpired}
              >
                <div className="flex min-w-0 flex-1">
                  {Array.from({ length: 3 }, (_, index) => (
                    <OTPField.Input
                      key={index}
                      autoFocus={index === 0}
                      className="font-inherit m-0 box-border h-10 w-full min-w-0 flex-1 border border-zinc-950/20 bg-transparent text-center text-lg/5 font-medium outline-none not-last:border-e-0 first:rounded-s-md last:rounded-e-md focus:z-1 focus:outline-2 focus:-outline-offset-1 focus:outline-[#0068d8] focus:outline-solid dark:border-zinc-700"
                      aria-label={`Character ${index + 1} of 6`}
                    />
                  ))}
                </div>
                <OTPField.Separator className="flex items-center justify-center text-zinc-600">
                  <Valkyrie icon={viMin} />
                </OTPField.Separator>
                <div className="flex min-w-0 flex-1">
                  {Array.from({ length: 3 }, (_, index) => (
                    <OTPField.Input
                      key={index + 3}
                      className="font-inherit m-0 box-border h-10 w-full min-w-0 flex-1 border border-zinc-950/20 bg-transparent text-center text-lg/5 font-medium outline-none not-last:border-e-0 first:rounded-s-md last:rounded-e-md focus:z-1 focus:outline-2 focus:-outline-offset-1 focus:outline-[#0068d8] focus:outline-solid dark:border-zinc-700"
                      aria-label={`Character ${index + 4} of 6`}
                    />
                  ))}
                </div>
              </OTPField.Root>
            </div>
          ) : (
            <div className="flex w-full max-w-80 min-w-0 flex-col items-start gap-1">
              <label htmlFor="otp" className="text-xs font-normal text-zinc-500 dark:text-zinc-400">
                Recovery code *
              </label>
              <OTPField.Root
                id="otp"
                length={10}
                className="flex w-full min-w-0 items-center"
                value={verificationCode}
                onValueChange={(value) => setVerificationCode(value)}
                validationType="alphanumeric"
                normalizeValue={(value) => value.toUpperCase()}
                disabled={totpExpired}
              >
                {Array.from({ length: 10 }, (_, index) => (
                  <OTPField.Input
                    key={index}
                    autoFocus={index === 0}
                    className="font-inherit m-0 box-border h-10 w-full min-w-0 flex-1 border border-zinc-950/20 bg-transparent text-center text-lg/5 font-medium outline-none not-last:border-e-0 first:rounded-s-md last:rounded-e-md focus:z-1 focus:outline-2 focus:-outline-offset-1 focus:outline-[#0068d8] focus:outline-solid dark:border-zinc-700"
                    aria-label={`Character ${index + 1} of 10`}
                  />
                ))}
              </OTPField.Root>
            </div>
          )}
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TotpTimer expires={totpChallenge.expires} />
            <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
              <Link
                onClick={cancelAuthentication}
                color="inherit"
                underline="hover"
                variant="body2"
                sx={{ cursor: "pointer" }}
              >
                Cancel
              </Link>
              <span> &middot;</span>
              <Link
                onClick={() => setRecoveryMode((prev) => !prev)}
                color="inherit"
                underline="hover"
                variant="body2"
                sx={{ cursor: "pointer" }}
              >
                {recoveryMode ? "Use verification code" : "Use recovery code"}
              </Link>
            </Stack>
          </Stack>
          <Button
            key="totpSubmit"
            variant="contained"
            size="large"
            id="submit"
            type="submit"
            endIcon={<Valkyrie icon={loading ? viSpinnerThird : viArrowRight} spin={loading} />}
            disabled={
              loading ||
              !(
                (recoveryMode === false && verificationCode.length === 6) ||
                (recoveryMode && verificationCode.length === 10)
              ) ||
              totpExpired
            }
          >
            Continue
          </Button>
        </AuthUI>
      )}
    </>
  );
}
